import { useEffect, useState } from 'react'
import { Download, Share, X } from 'lucide-react'
import { usePWAInstall } from '../utils/usePWAInstall'

const INSTALL_DAY_KEY = 'install_prompt_day'

function today() {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function shownToday() {
  try { return localStorage.getItem(INSTALL_DAY_KEY) === today() } catch { return false }
}

// Small detection helpers, consistent with usePushNotifications.
function isIOS() {
  try { return /iphone|ipad|ipod/i.test(window.navigator.userAgent || '') } catch { return false }
}
function isStandalone() {
  try {
    return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
      || window.navigator.standalone === true // iOS home-screen PWA
  } catch { return false }
}

/**
 * Once-a-day centered modal nudging the user to install the PWA. Works for
 * GUESTS (no auth gate). Shows at most once per calendar day across pages
 * (localStorage gate), and never when already running as an installed PWA.
 *
 * - Android/desktop (canInstall): a "Yüklə" button calls install().
 * - iOS Safari (no canInstall): manual Share → "Ana ekrana əlavə et" steps.
 *
 * The complementary bottom bar (MobileAppNudge) stays as-is; this is the
 * stronger once-a-day prompt.
 */
export default function InstallPromptDaily() {
  const { canInstall, install } = usePWAInstall()

  // Decide visibility once on mount, then again if canInstall flips
  // (beforeinstallprompt can fire shortly after load).
  const [standalone] = useState(isStandalone)
  const [ios] = useState(() => isIOS() && !isStandalone())
  const [dismissedToday, setDismissedToday] = useState(shownToday)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (standalone) return          // already installed → never show
    if (dismissedToday) return      // already shown/dismissed today
    if (canInstall || ios) setOpen(true)
  }, [standalone, dismissedToday, canInstall, ios])

  const close = () => {
    try { localStorage.setItem(INSTALL_DAY_KEY, today()) } catch {}
    setDismissedToday(true)
    setOpen(false)
  }

  const onInstall = async () => {
    await install()
    close()
  }

  if (!open) return null

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && close()}
      style={{
        position: 'fixed', inset: 0, zIndex: 2400,
        background: 'rgba(0,0,0,.78)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: '100%', maxWidth: 420, padding: '1.75rem', position: 'relative' }}
      >
        <button
          onClick={close}
          aria-label="Bağla"
          style={{
            position: 'absolute', top: '.75rem', right: '.75rem',
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--muted)', padding: '.25rem',
            display: 'flex', alignItems: 'center',
          }}
        >
          <X size={18} />
        </button>

        <div style={{ fontSize: '2.4rem', marginBottom: '.85rem', lineHeight: 1 }}>📲</div>

        <h2 style={{ fontWeight: 800, fontSize: '1.15rem', margin: '0 0 .55rem', color: 'var(--text)' }}>
          1ishvar.az-ı tətbiq kimi yükləyin
        </h2>
        <p style={{ fontSize: '.86rem', color: 'var(--muted)', lineHeight: 1.65, margin: '0 0 1.25rem' }}>
          Daha sürətli, oflayn işləyir və bildiriş alırsınız.
        </p>

        {canInstall ? (
          <button onClick={onInstall} className="btn btn-primary btn-full">
            <Download size={16} style={{ marginRight: 6 }} /> Yüklə
          </button>
        ) : ios ? (
          <>
            <div
              style={{
                display: 'flex', alignItems: 'flex-start', gap: '.6rem',
                background: 'rgba(var(--accent-rgb),.07)', border: '1px solid rgba(var(--accent-rgb),.18)',
                borderRadius: 8, padding: '.75rem .9rem', marginBottom: '1.1rem',
                fontSize: '.83rem', color: 'var(--text)', lineHeight: 1.6,
              }}
            >
              <Share size={18} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: '.1rem' }} />
              <span>
                Safari-də <strong>Paylaş</strong> düyməsinə basın →{' '}
                <strong>"Ana ekrana əlavə et"</strong> seçin.
              </span>
            </div>
            <button onClick={close} className="btn btn-primary btn-full">
              Anladım
            </button>
          </>
        ) : null}

        <button
          onClick={close}
          className="btn btn-ghost btn-full"
          style={{ marginTop: '.6rem' }}
        >
          Sonra
        </button>
      </div>
    </div>
  )
}
